import React, { useState } from 'react';

export default function StepFour({ valuation, businessAnalysis, onReset, onBack }) {
  const [showExcelView, setShowExcelView] = useState(false);
  const [showFullDescription, setShowFullDescription] = useState(false);

  if (!valuation) {
    return (
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">No valuation data available</h2>
        <button 
          onClick={onReset}
          className="btn-primary"
        >
          Start Over
        </button>
      </div>
    );
  }

  const formatCurrency = (amount) => {
    if (amount >= 1000000) {
      return `$${(amount / 1000000).toFixed(1)}M`;
    } else if (amount >= 1000) {
      return `$${(amount / 1000).toFixed(0)}K`;
    }
    return `$${amount?.toLocaleString() || '0'}`;
  };

  const formatFullCurrency = (amount) => {
    return `$${amount?.toLocaleString() || '0'}`;
  };

  // Excel-style valuation data
  const excelData = {
    preTaxProfit: valuation.preTaxProfit || 0,
    ebitda: valuation.ebitda || 0,
    multiple: valuation.multiple || 0,
    baseMultiple: valuation.baseMultiple || 0,
    growthScore: valuation.growthScore || 0,
    growthAdjustment: valuation.growthAdjustment || 0,
    recurringAdjustment: valuation.recurringAdjustment || 0,
    baseValuation: valuation.baseValuation || 0,
    adjustedValuation: valuation.adjustedValuation || 0,
    conservativeValue: valuation.adjustedValuation * 0.85,
    optimisticValue: valuation.adjustedValuation * 1.15,
    recurringRevenue: valuation.recurringRevenue || 90,
    growthTrend: valuation.growthTrend || 'Increasing'
  };

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header */}
      <div className="text-center mb-12">
        <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
          Your preliminary valuation is ready!
        </h1>
        <p className="text-xl text-gray-600">
          Based on comprehensive business analysis and market data
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Main Valuation Display */}
        <div className="lg:col-span-2">
          {/* Valuation Range Card */}
          <div className="card-premium p-8 mb-8">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Estimated Value Range</h2>
              <div className="text-5xl font-bold text-gray-900 mb-4">
                {formatCurrency(excelData.conservativeValue)} - {formatCurrency(excelData.optimisticValue)}
              </div>
              <p className="text-gray-600 mb-6">Valuation based on:</p>
              <div className="flex justify-center space-x-6 text-sm">
                <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full">Business Details</span>
                <span className="bg-emerald-100 text-emerald-800 px-3 py-1 rounded-full">Similar Deals</span>
                <span className="bg-purple-100 text-purple-800 px-3 py-1 rounded-full">Market Trends</span>
              </div>
            </div>

            {/* Visual Range Display */}
            <div className="relative mb-8">
              <div className="flex justify-between text-sm text-gray-600 mb-2">
                <span>{formatCurrency(excelData.conservativeValue)}</span>
                <span>{formatCurrency(excelData.optimisticValue)}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-8 relative overflow-hidden">
                <div className="bg-gradient-to-r from-blue-500 to-emerald-500 h-full rounded-full"></div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-white font-semibold text-sm">
                    {formatCurrency(excelData.adjustedValuation)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Valuation Approaches */}
          <div className="card-premium p-8 mb-8">
            <h3 className="text-xl font-bold text-gray-900 mb-6">Valuation Overview</h3>
            <p className="text-gray-600 mb-6">Your business valuation across three industry-standard approaches</p>
            
            <div className="space-y-4">
              {/* Revenue-based */}
              <div className={`flex items-center justify-between p-4 rounded-lg ${
                excelData.ebitda < 500000 ? 'bg-blue-50 border-2 border-blue-200' : 'bg-gray-50'
              }`}>
                <div>
                  <span className="font-medium text-gray-900">Revenue-based</span>
                  <span className="text-sm text-gray-600 ml-4">0.6x - 0.9x</span>
                </div>
                <div className="flex items-center">
                  <div className="w-32 bg-gray-200 rounded-full h-2 mr-4">
                    <div className="bg-blue-500 h-2 rounded-full" style={{width: '70%'}}></div>
                  </div>
                  <span className="font-semibold text-gray-900 w-20 text-right">
                    {formatCurrency(excelData.adjustedValuation * 0.7)} - {formatCurrency(excelData.adjustedValuation * 0.9)}
                  </span>
                </div>
              </div>

              {/* SDE-based */}
              <div className={`flex items-center justify-between p-4 rounded-lg ${
                excelData.ebitda >= 500000 && excelData.ebitda < 1000000 ? 'bg-emerald-50 border-2 border-emerald-200' : 'bg-gray-50'
              }`}>
                <div>
                  <span className="font-medium text-gray-900">SDE-based</span>
                  <span className="text-sm text-gray-600 ml-4">2.9x - 3.9x</span>
                </div>
                <div className="flex items-center">
                  <div className="w-32 bg-gray-200 rounded-full h-2 mr-4">
                    <div className="bg-emerald-500 h-2 rounded-full" style={{width: '85%'}}></div>
                  </div>
                  <span className="font-semibold text-gray-900 w-20 text-right">
                    {formatCurrency(excelData.adjustedValuation * 0.8)} - {formatCurrency(excelData.adjustedValuation * 1.1)}
                  </span>
                </div>
              </div>

              {/* EBITDA-based - Primary for larger businesses */}
              <div className={`flex items-center justify-between p-4 rounded-lg ${
                excelData.ebitda >= 1000000 ? 'bg-blue-50 border-2 border-blue-200' : 'bg-gray-50'
              }`}>
                <div>
                  <span className="font-medium text-gray-900">EBITDA-based</span>
                  <span className={`text-sm ml-4 ${
                    excelData.ebitda >= 1000000 ? 'text-blue-600' : 'text-gray-600'
                  }`}>{excelData.multiple.toFixed(1)}x - {(excelData.multiple * 1.2).toFixed(1)}x</span>
                </div>
                <div className="flex items-center">
                  <div className="w-32 bg-gray-200 rounded-full h-2 mr-4">
                    <div className="bg-blue-600 h-2 rounded-full" style={{width: '100%'}}></div>
                  </div>
                  <span className="font-semibold text-gray-900 w-20 text-right">
                    {formatCurrency(excelData.conservativeValue)} - {formatCurrency(excelData.optimisticValue)}
                  </span>
                </div>
              </div>
            </div>

            <div className="mt-6 p-4 bg-blue-50 rounded-lg">
              <div className="flex items-start">
                <div className="bg-blue-100 rounded-full p-2 mr-3">
                  <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Valuation approach</h4>
                  <p className="text-sm text-gray-700">
                    {excelData.ebitda < 500000 
                      ? "Smaller businesses are typically valued based on revenue multiples."
                      : excelData.ebitda < 1000000
                      ? "Mid-sized businesses are often valued using SDE (Seller's Discretionary Earnings)."
                      : "Businesses that generate more than $1.0M in EBITDA are typically valued based on the EBITDA approach."
                    }
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Key Valuation Drivers */}
          <div className="card-premium p-8 mb-8">
            <h3 className="text-xl font-bold text-gray-900 mb-6">Key Valuation Drivers</h3>
            <p className="text-gray-600 mb-6">Specific factors that can affect your business valuation</p>
            
            <div className="space-y-6">
              {valuation.drivers?.map((driver, index) => (
                <div key={index} className="flex items-start">
                  <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center mr-4 ${
                    driver.impact === 'positive' ? 'bg-green-100' : 'bg-red-100'
                  }`}>
                    {driver.impact === 'positive' ? (
                      <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                    ) : (
                      <svg className="w-4 h-4 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-semibold text-gray-900">{driver.factor}</h4>
                      <span className={`text-sm font-medium px-2 py-1 rounded ${
                        driver.impact === 'positive' 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {driver.impact === 'positive' ? 'Positive impact' : 'Negative impact'}
                      </span>
                    </div>
                    <p className="text-gray-600">{driver.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Excel View Toggle */}
          <div className="text-center mb-8">
            <button
              onClick={() => setShowExcelView(!showExcelView)}
              className="btn-secondary mr-4"
            >
              {showExcelView ? 'Hide' : 'Show'} Excel-Style Valuation
            </button>
            <button
              onClick={onReset}
              className="btn-primary"
            >
              Get Another Valuation
            </button>
          </div>

          {/* Excel-Style Valuation Table */}
          {showExcelView && (
            <div className="card-premium p-8 mb-8 bg-gray-50">
              <h3 className="text-xl font-bold text-gray-900 mb-6">Detailed Valuation Method</h3>
              <div className="bg-white rounded-lg overflow-hidden border">
                <div className="bg-gray-800 text-white p-4">
                  <h4 className="font-bold text-white">Valuation Method - AMIVA Equity Partners</h4>
                  <p className="text-sm text-gray-300 mt-1">
                    Enterprise Value calculation based on adjusted EBITDA and industry multiples
                  </p>
                </div>
                
                <div className="p-6">
                  {/* Description */}
                  <div className="mb-6 p-4 bg-blue-50 rounded-lg">
                    <p className="text-sm text-gray-700">
                      In order to determine the purchase price of your company, we must first understand its Enterprise Value. 
                      This is most commonly calculated in the private markets by looking at the adjusted earnings of your company 
                      and multiplying them by a certain number. The adjusted earnings are known as "Adj. EBITDA" and the multiplier 
                      as the "Multiple". The Multiple is a reflection of what other similar companies have been sold for in the past year.
                    </p>
                  </div>

                  {/* Main Calculation Table */}
                  <div className="grid grid-cols-3 gap-4 mb-6">
                    <div className="bg-gray-100 p-3 rounded">
                      <div className="text-sm font-medium text-gray-600">Checklist</div>
                    </div>
                    <div className="bg-gray-100 p-3 rounded text-center">
                      <div className="text-sm font-medium text-gray-600">Conservative</div>
                    </div>
                    <div className="bg-gray-100 p-3 rounded text-center">
                      <div className="text-sm font-medium text-gray-600">Expected</div>
                    </div>
                  </div>

                  <div className="space-y-2 mb-6">
                    <div className="grid grid-cols-3 gap-4 py-2">
                      <div className="text-sm">Pre-tax Profit</div>
                      <div className="text-center font-mono">{formatFullCurrency(excelData.preTaxProfit)}</div>
                      <div className="text-center font-mono">{formatFullCurrency(excelData.preTaxProfit)}</div>
                    </div>
                    <div className="grid grid-cols-3 gap-4 py-2 bg-blue-50">
                      <div className="text-sm font-semibold">Adj. EBITDA (Pre-tax × 1.1)</div>
                      <div className="text-center font-mono font-semibold">{formatFullCurrency(excelData.ebitda)}</div>
                      <div className="text-center font-mono font-semibold">{formatFullCurrency(excelData.ebitda)}</div>
                    </div>
                    <div className="grid grid-cols-3 gap-4 py-2">
                      <div className="text-sm">3 Year Growth Trend Score</div>
                      <div className="text-center">{excelData.growthTrend}</div>
                      <div className="text-center">{excelData.growthTrend}</div>
                    </div>
                    <div className="grid grid-cols-3 gap-4 py-2 bg-gray-50">
                      <div className="text-sm">Recurring Revenue Percentage (0%-100%)</div>
                      <div className="text-center">{excelData.recurringRevenue}%</div>
                      <div className="text-center">{excelData.recurringRevenue}%</div>
                    </div>
                  </div>

                  {/* Sensitivity Analysis */}
                  <div className="mb-6">
                    <h5 className="font-semibold text-gray-900 mb-3">Sensitivity Analysis: Adj. EBITDA vs Multiple Applied</h5>
                    <div className="bg-gray-800 text-white p-4 rounded">
                      <div className="grid grid-cols-4 gap-4 text-center text-sm">
                        <div className="font-bold text-white">Valuation Sensitivity Table</div>
                        <div className="font-bold text-white">{excelData.multiple.toFixed(1)}</div>
                        <div className="font-bold text-white">{(excelData.multiple * 1.1).toFixed(1)}</div>
                        <div className="font-bold text-white">{(excelData.multiple * 1.2).toFixed(1)}</div>
                      </div>
                      <div className="grid grid-cols-4 gap-4 text-center text-sm mt-2">
                        <div className="text-white">{formatFullCurrency(excelData.ebitda)}</div>
                        <div className="text-white">{formatCurrency(excelData.conservativeValue)}</div>
                        <div className="text-white">{formatCurrency(excelData.adjustedValuation)}</div>
                        <div className="text-white">{formatCurrency(excelData.optimisticValue)}</div>
                      </div>
                    </div>
                  </div>

                  {/* Valuation Multiple Details */}
                  <div className="mt-6 pt-6 border-t">
                    <h5 className="font-semibold text-gray-900 mb-3">Adjusted Valuation Multiple Calculation</h5>
                    <div className="bg-gray-800 text-white p-4 rounded">
                      <div className="grid grid-cols-3 gap-4 text-center mb-4">
                        <div>
                          <div className="text-2xl font-bold">{excelData.baseMultiple.toFixed(3)}x</div>
                          <div className="text-sm text-gray-300">Base Multiple</div>
                          <div className="text-xs text-gray-400 mt-1">Based on EBITDA</div>
                        </div>
                        <div>
                          <div className="text-2xl font-bold">{excelData.growthScore > 0 ? '+' : ''}{excelData.growthScore}</div>
                          <div className="text-sm text-gray-300">Growth Score</div>
                          <div className="text-xs text-gray-400 mt-1">{excelData.growthTrend}</div>
                        </div>
                        <div>
                          <div className="text-2xl font-bold">{excelData.recurringRevenue}%</div>
                          <div className="text-sm text-gray-300">Recurring Revenue</div>
                          <div className="text-xs text-gray-400 mt-1">of total revenue</div>
                        </div>
                      </div>
                      <div className="border-t border-gray-600 pt-3">
                        <div className="text-center">
                          <div className="text-sm text-gray-300 mb-2">
                            Adjusted Multiple = Base + (0.5 × Growth Score) + (Recurring% × Base × 0.2)
                          </div>
                          <div className="text-xs text-gray-400 mb-2">
                            = {excelData.baseMultiple.toFixed(3)} + (0.5 × {excelData.growthScore}) + ({(excelData.recurringRevenue/100).toFixed(2)} × {excelData.baseMultiple.toFixed(3)} × 0.2)
                          </div>
                          <div className="text-xs text-gray-400 mb-2">
                            = {excelData.baseMultiple.toFixed(3)} + {excelData.growthAdjustment.toFixed(1)} + {excelData.recurringAdjustment.toFixed(3)}
                          </div>
                          <div className="text-3xl font-bold">{excelData.multiple.toFixed(2)}x</div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Final Valuation */}
                  <div className="border-t pt-6">
                    <h5 className="font-semibold text-gray-900 mb-3">General Financial Purchase Price ("Enterprise Value")</h5>
                    <div className="space-y-2">
                      <div className="flex justify-between py-2 bg-red-50 px-4 rounded">
                        <span>Worst Case</span>
                        <span className="font-mono font-bold">{formatFullCurrency(excelData.conservativeValue)}</span>
                      </div>
                      <div className="flex justify-between py-2 bg-blue-50 px-4 rounded">
                        <span>Base Case</span>
                        <span className="font-mono font-bold">{formatFullCurrency(excelData.adjustedValuation)}</span>
                      </div>
                      <div className="flex justify-between py-2 bg-green-50 px-4 rounded">
                        <span>Positive Case</span>
                        <span className="font-mono font-bold">{formatFullCurrency(excelData.optimisticValue)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Revenue Breakdown */}
                  <div className="mt-6 pt-6 border-t">
                    <h5 className="font-semibold text-gray-900 mb-3">Revenue Breakdown</h5>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-green-100 p-3 rounded">
                        <div className="text-sm text-gray-600">Recurring</div>
                        <div className="font-bold text-green-800">{excelData.recurringRevenue}%</div>
                      </div>
                      <div className="bg-orange-100 p-3 rounded">
                        <div className="text-sm text-gray-600">One Off</div>
                        <div className="font-bold text-orange-800">{100 - excelData.recurringRevenue}%</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Business Summary Sidebar */}
        <div className="lg:col-span-1">
          <div className="space-y-6">
            <div className="card-premium p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">
              {businessAnalysis?.websiteData?.title?.split(' - ')[0] || 'Your Business'}
            </h3>
            <div className="text-gray-600 mb-4">
              <p className={showFullDescription ? '' : 'line-clamp-3'}>
                {businessAnalysis?.overview}
              </p>
              {businessAnalysis?.overview?.length > 150 && (
                <button
                  onClick={() => setShowFullDescription(!showFullDescription)}
                  className="text-blue-600 hover:text-blue-800 text-sm font-medium mt-2"
                >
                  {showFullDescription ? 'View less' : 'View more'}
                </button>
              )}
            </div>
            
            <div className="space-y-3 mb-6">
              <div className="flex items-center text-sm">
                <svg className="w-4 h-4 text-gray-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                </svg>
                <span className="text-gray-600">{businessAnalysis?.location || 'United States'}</span>
              </div>
              <div className="flex items-center text-sm">
                <svg className="w-4 h-4 text-gray-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="text-gray-600">{businessAnalysis?.websiteData?.url}</span>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4 mb-6 text-center">
              <div>
                <div className="text-lg font-bold text-gray-900">
                  {formatCurrency(valuation?.revenue || 0)}
                </div>
                <div className="text-xs text-gray-600">Revenue</div>
              </div>
              <div>
                <div className="text-lg font-bold text-gray-900">
                  {formatCurrency(valuation?.ebitda || 0)}
                </div>
                <div className="text-xs text-gray-600">EBITDA</div>
              </div>
              <div>
                <div className="text-lg font-bold text-gray-900">
                  {formatCurrency(valuation?.sde || 0)}
                </div>
                <div className="text-xs text-gray-600">SDE</div>
              </div>
            </div>
            </div>
            
            {/* AMIVA CTA - Sticky Booking Card */}
            <div className="lg:sticky lg:top-8">
              <div className="card-premium p-0 overflow-hidden border-0 shadow-xl">
                {/* Header section with gradient */}
                <div className="p-6 pb-4" style={{ 
                  background: 'linear-gradient(135deg, #F9FAFB 0%, #F3F4F6 100%)'
                }}>
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="text-xl font-bold text-gray-900">
                      Ready to explore your options?
                    </h4>
                    <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                      <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M2 5a2 2 0 012-2h7a2 2 0 012 2v4a2 2 0 01-2 2H9l-3 3v-3H4a2 2 0 01-2-2V5z" />
                        <path d="M15 7v2a4 4 0 01-4 4H9.828l-1.766 1.767c.28.149.599.233.938.233h2l3 3v-3h2a2 2 0 002-2V9a2 2 0 00-2-2h-1z" />
                      </svg>
                    </div>
                  </div>
                  
                  <p className="text-gray-600 text-sm leading-relaxed">
                    Get expert guidance on maximizing your business value and exploring exit opportunities.
                  </p>
                </div>
                
                {/* Profile section */}
                <div className="px-6 py-4 bg-white">
                  <div className="flex items-center">
                    <img 
                      src="/mike-pantouvakis.jpg" 
                      alt="Mike Pantouvakis"
                      className="w-14 h-14 rounded-full object-cover ring-2 ring-gray-200 mr-4"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                    <div 
                      className="w-14 h-14 rounded-full ring-2 ring-gray-200 mr-4 bg-gradient-to-br from-blue-500 to-blue-600 items-center justify-center text-white font-semibold text-lg"
                      style={{ display: 'none' }}
                    >
                      MP
                    </div>
                    <div className="flex-1">
                      <div className="font-semibold text-gray-900">Mike Pantouvakis</div>
                      <div className="text-sm text-gray-600">Founder & Managing Principal</div>
                      <div className="flex items-center mt-1">
                        <div className="flex text-yellow-400 text-xs">
                          <span>★</span><span>★</span><span>★</span><span>★</span><span>★</span>
                        </div>
                        <span className="text-gray-500 text-xs ml-2">150+ successful exits</span>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* CTA section */}
                <div className="p-6 pt-4 bg-white border-t border-gray-100">
                  <button 
                    onClick={() => {
                      const bookingSection = document.querySelector('#booking-section');
                      if (bookingSection) {
                        bookingSection.scrollIntoView({ behavior: 'smooth' });
                      }
                    }}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-4 rounded-lg transition-all duration-200 hover:shadow-lg"
                  >
                    Schedule Free 15-Min Call
                  </button>
                  
                  <div className="mt-4 space-y-2">
                    <div className="flex items-center text-gray-600 text-xs">
                      <svg className="w-4 h-4 mr-2 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      100% Confidential
                    </div>
                    <div className="flex items-center text-gray-600 text-xs">
                      <svg className="w-4 h-4 mr-2 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      No obligation to proceed
                    </div>
                    <div className="flex items-center text-gray-600 text-xs">
                      <svg className="w-4 h-4 mr-2 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      Expert M&A guidance
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Expert Guidance & Booking Section */}
      <div id="booking-section" className="mt-16 bg-gradient-to-br from-gray-50 to-white">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left side - Content */}
          <div className="p-8 lg:p-12">
            <div className="mb-6">
              <div className="inline-flex items-center bg-amiva-gradient-subtle text-amiva-primary text-sm font-semibold px-4 py-2 rounded-full mb-4">
                <span className="text-xs mr-2">💰</span>
                Your preliminary valuation: <span className="ml-2 bg-gray-800 text-white px-3 py-1 rounded-full">
                  {formatCurrency(excelData.conservativeValue)} - {formatCurrency(excelData.optimisticValue)}
                </span>
              </div>
            </div>
            
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6 leading-tight">
              Every strong exit starts with expert guidance
            </h2>
            
            <p className="text-xl text-gray-600 mb-8 leading-relaxed">
              Connect with an AMIVA M&A expert to fine-tune your valuation
            </p>
            
            <div className="space-y-4 mb-8">
              <div className="flex items-center">
                <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center mr-4">
                  <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
                <span className="text-lg text-gray-700">Calibrate your valuation</span>
              </div>
              
              <div className="flex items-center">
                <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center mr-4">
                  <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
                <span className="text-lg text-gray-700">Discuss potential buyers</span>
              </div>
              
              <div className="flex items-center">
                <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center mr-4">
                  <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
                <span className="text-lg text-gray-700">Plan your ideal exit</span>
              </div>
            </div>
          </div>

          {/* Right side - Embedded Calendar */}
          <div className="bg-gray-800 rounded-3xl p-8 lg:p-10 shadow-2xl">
            <div className="text-center mb-8">
              <h3 className="text-2xl lg:text-3xl font-bold text-white mb-4">
                Schedule your call today
              </h3>
            </div>
            
            <div className="bg-white rounded-2xl overflow-hidden shadow-lg">
              <iframe 
                src="https://app.usemotion.com/meet/mike-pantouvakis/acquisition-discussion" 
                title="Schedule a call with Mike Pantouvakis"
                width="100%" 
                height="600px" 
                frameBorder="0"
                className="w-full"
                style={{ minHeight: '600px' }}
              />
            </div>
            
            <div className="text-center mt-6">
              <p className="text-gray-300 text-sm">
                No commitment and 100% confidential.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
} 